// Queue Types for Queue Monitoring Feature

export type QueueName = "rgs-queue" | "cms-queue";

export type JobState =
  | "waiting"
  | "active"
  | "completed"
  | "failed"
  | "delayed"
  | "paused";

export interface QueueStats {
  waiting: number;
  active: number;
  completed: number;
  failed: number;
  delayed: number;
  paused: number;
}

export interface QueueOverview {
  queueName: QueueName;
  stats: QueueStats;
  isPaused: boolean;
}

export interface JobBackoff {
  type: string;
  delay: number;
}

export interface JobOpts {
  attempts?: number;
  delay?: number;
  backoff?: JobBackoff;
}

export interface BaseJob {
  id: string;
  name: string;
  data: Record<string, any>;
  timestamp: number;
  attemptsMade: number;
  state: JobState;
  opts?: JobOpts;
}

export interface FailedJob extends BaseJob {
  failedReason?: string;
  stacktrace?: string[];
}

export interface DelayedJob extends BaseJob {
  delay?: number;
}

export interface JobDetail extends BaseJob {
  processedOn?: number;
  finishedOn?: number;
  failedReason?: string;
  stacktrace?: string[];
  returnvalue?: any;
}

export interface QueueJobsResponse {
  queueName: QueueName;
  jobs: FailedJob[] | DelayedJob[];
}

// API Response Types - Note: API uses 'status' field but we support both for compatibility
export interface QueueStatsResponse {
  success?: boolean;
  status?: boolean;
  message: string;
  data: QueueOverview[];
}

export interface SingleQueueStatsResponse {
  success?: boolean;
  status?: boolean;
  message: string;
  data: {
    queueName: QueueName;
    stats: QueueStats;
  };
}

export interface FailedJobsResponse {
  success?: boolean;
  status?: boolean;
  message: string;
  data: QueueJobsResponse[];
}

export interface PendingJobsResponse {
  success?: boolean;
  status?: boolean;
  message: string;
  data: QueueJobsResponse[];
}

export interface JobListResponse {
  success?: boolean;
  status?: boolean;
  message: string;
  data: {
    jobs: BaseJob[];
    total: number;
    start: number;
    limit: number;
  };
}

export interface JobDetailResponse {
  success?: boolean;
  status?: boolean;
  message: string;
  data: JobDetail;
}

export interface JobActionResponse {
  success?: boolean;
  status?: boolean;
  message: string;
  data: {
    jobId: string;
    queueName: QueueName;
  };
}

export interface CleanQueueResponse {
  success?: boolean;
  status?: boolean;
  message: string;
  data: {
    removed: number;
  };
}

export interface QueuePauseResumeResponse {
  success?: boolean;
  status?: boolean;
  message: string;
  data: {
    queueName: QueueName;
    paused: boolean;
  };
}

// Request Types
export interface GetJobsParams {
  queueName: QueueName;
  state?: JobState;
  jobName?: string;
  start?: number;
  limit?: number;
}

export interface CleanQueueParams {
  queueName: QueueName;
  state: "completed" | "failed";
  gracePeriodMs?: number;
}

// Job name display mappings
export const JOB_NAME_LABELS: Record<string, string> = {
  "retry-credit": "Retry Credit",
  "retry-refund": "Retry Refund",
  "retry-complete-game": "Retry Complete Game",
  "email-sending": "Email Sending",
  "slack-message": "Slack Message",
  "export-cleanup": "Export Cleanup",
};

export const QUEUE_LABELS: Record<QueueName, string> = {
  "rgs-queue": "RGS Queue",
  "cms-queue": "CMS Queue",
};

export const JOB_STATE_COLORS: Record<JobState, string> = {
  waiting: "#ff9800",
  active: "#2196f3",
  completed: "#4caf50",
  failed: "#f44336",
  delayed: "#9c27b0",
  paused: "#9e9e9e",
};
